import React, { useCallback, useState, useEffect } from "react";
import {
	Card,
	Typography,
	Table,
	Button,
	Space,
	Spin,
	Alert,
	Row,
	Col,
	Breadcrumb,
	Progress,
} from "antd";
import {
	PlayCircleOutlined,
	BookOutlined,
	ClockCircleOutlined,
	VideoCameraOutlined,
	HomeOutlined,
	ArrowLeftOutlined,
	LockOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import useAxios from "../../utilities/useAxios";
import { formatDuration } from "../../utilities/Utils";

import PageTitle from "../PageTitle";

const { Title, Text } = Typography;

const CourseVideoListPage = () => {
	const [videos, setVideos] = useState([]);
	const [course, setCourse] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const navigate = useNavigate();
	const { courseId } = useParams();

	const fetchCourseVideos = useCallback(async () => {
		try {
			setLoading(true);
			// Get course lectures and their videos
			const response = await useAxios.get(
				`/cma/courses/${courseId}/videos/`
			);
			setVideos(response.data);

			// Get course details from the first video or create a basic course object
			if (response.data.length > 0) {
				const firstVideo = response.data[0];
				setCourse({
					id: courseId,
					name:
						firstVideo.lecture_name?.split(
							" - "
						)[0] || "강의",
					book: { title: "교재" },
				});
			}

			setError(null);
		} catch (error) {
			console.error("Error fetching course videos:", error);
			setError("강의 영상 목록을 불러오는데 실패했습니다.");
		} finally {
			setLoading(false);
		}
	}, [courseId]);

	useEffect(() => {
		if (courseId) {
			fetchCourseVideos();
		}
	}, [courseId, fetchCourseVideos]);

	const handleVideoSelect = (video) => {
		navigate(`/cma/courses/${courseId}/videos/${video.id}`);
	};

	const handleBackToEnrollments = () => {
		navigate("/cma");
	};

	const getProgressColor = (progress) => {
		if (progress >= 80) return "green";
		if (progress >= 50) return "orange";
		return "blue";
	};

	// Breadcrumb items configuration
	const breadcrumbItems = [
		{
			title: (
				<Button
					type="link"
					icon={<HomeOutlined />}
					onClick={() => navigate("/cma")}
					style={{ padding: 0 }}>
					CMA
				</Button>
			),
		},
		{
			title: (
				<Button
					type="link"
					icon={<ArrowLeftOutlined />}
					onClick={handleBackToEnrollments}
					style={{ padding: 0 }}>
					수강 신청 내역
				</Button>
			),
		},
		{
			title: "강의 영상 목록",
		},
	];

	const columns = [
		{
			title: "강의명",
			dataIndex: "lecture_name",
			key: "lecture_name",
			render: (text, record) => (
				<Space>
					<BookOutlined />
					<Text strong>{text}</Text>
				</Space>
			),
		},
		{
			title: "영상 제목",
			dataIndex: "name",
			key: "name",
			render: (text, record) => (
				<Space>
					{record.is_locked && (
						<LockOutlined
							style={{
								color: "#ff4d4f",
							}}
						/>
					)}
					<Button
						type="link"
						onClick={() =>
							!record.is_locked &&
							handleVideoSelect(
								record
							)
						}
						disabled={record.is_locked}
						style={{
							padding: 0,
							height: "auto",
						}}>
						{text}
					</Button>
				</Space>
			),
		},
		{
			title: "재생 시간",
			dataIndex: "duration",
			key: "duration",
			render: (duration) => (
				<Space>
					<ClockCircleOutlined />
					{formatDuration(duration)}
				</Space>
			),
		},
		{
			title: "진도율",
			dataIndex: "progress",
			key: "progress",
			render: (progress) => {
				if (
					progress === null ||
					progress === undefined
				) {
					return (
						<Text type="secondary">
							미시청
						</Text>
					);
				}
				return (
					<div style={{ width: "120px" }}>
						<Progress
							percent={progress}
							size="small"
							strokeColor={getProgressColor(
								progress
							)}
							format={(percent) =>
								`${percent}%`
							}
						/>
					</div>
				);
			},
		},
		{
			title: "시청",
			key: "actions",
			render: (_, record) => {
				if (record.is_locked) {
					return (
						<Space>
							<LockOutlined
								style={{
									color: "#ff4d4f",
								}}
							/>
							<Text type="secondary">
								{
									record.unlock_week
								}
								주차에
								공개됩니다
							</Text>
						</Space>
					);
				}
				return (
					<Button
						type="primary"
						size="small"
						icon={<PlayCircleOutlined />}
						onClick={() =>
							handleVideoSelect(
								record
							)
						}>
						시청하기
					</Button>
				);
			},
		},
	];

	if (loading) {
		return (
			<div style={{ textAlign: "center", padding: "50px" }}>
				<Spin size="large" />
			</div>
		);
	}

	return (
		<div style={{ padding: "24px" }}>
			<PageTitle title="기독사관학교 - 강의 영상 목록" />
			{/* Breadcrumb Navigation */}
			<Breadcrumb
				items={breadcrumbItems}
				style={{ marginBottom: "16px" }}
			/>

			{/* Page Header */}
			<Card style={{ marginBottom: "24px" }}>
				<Row gutter={[16, 16]} align="middle">
					<Col flex="auto">
						<Title
							level={2}
							style={{ margin: 0 }}>
							<VideoCameraOutlined
								style={{
									marginRight:
										"8px",
								}}
							/>
							{course?.name || "강의"}{" "}
							- 강의 영상 목록
						</Title>
						<Text type="secondary">
							총 {videos.length}개의
							영상이 있습니다.
						</Text>
					</Col>
					<Col>
						<Button
							type="primary"
							icon={
								<ArrowLeftOutlined />
							}
							onClick={
								handleBackToEnrollments
							}>
							수강 신청 내역으로
							돌아가기
						</Button>
					</Col>
				</Row>
			</Card>

			{/* Error Display */}
			{error && (
				<Alert
					message="오류"
					description={error}
					type="error"
					showIcon
					className="mb-3"
				/>
			)}

			{/* Course Videos Table */}
			<Card>
				{videos.length === 0 ? (
					<Alert
						message="알림"
						description="이 강의에는 아직 등록된 영상이 없습니다."
						type="info"
						showIcon
					/>
				) : (
					<Table
						columns={columns}
						dataSource={videos}
						rowKey="id"
						pagination={{
							pageSize: 20,
							showSizeChanger: true,
							showQuickJumper: true,
							showTotal: (
								total,
								range
							) =>
								`${range[0]}-${range[1]} / 총 ${total}개`,
						}}
					/>
				)}
			</Card>
		</div>
	);
};

export default CourseVideoListPage;
